/* eslint-disable max-len */
import React, {
  useEffect, useMemo, useRef, memo,
  useCallback,
  useState,
} from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { withStyles } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';
import classNames from 'classnames';
import {
  getEquipmentStatuses,
  setCurrentEquipmentStatusFilterV3,
} from '~/store/dispatch/actions';
import {
  getCodeGroups,
  getCodes,
  getCodeTypes,
  getElements,
  getEquipmentGroups,
  getEquipments,
  getEquipmentTypes,
  getSubElements,
  getNrefOperators,
} from '~/store/nref/actions';
import { getClosures } from '~/store/manager/actions';
import { buildCodeKey, toCodesMapByKey } from '~/utils/codes';
import { getFilters } from '~/services/cache';
import usePlatformConfiguration from '~/services/platformConfiguration';
import EquipmentStatusFilters from './EquipmentFilters';
import EquipmentTypeList from './EquipmentTypeList';
import {
  mountData,
  formatFilterOptions,
  filterEquipments,
} from './utils/equipmentStatusListUtils';
import { useEquipmentStatusFilters } from './hooks/useEquipmentStatusFilters';
import styles from './styles';
import EquipmentStatusLegend from './EquipmentStatusLegend';
import useThrottledRealtimeRefresh from '../useMqtt/useThrottledRealtimeRefresh';
import useRealtimeSignal from '../useMqtt/useRealtimeSignal';

const EquipmentStatusV3 = memo(({ classes }) => {
  const dispatch = useDispatch();
  const handleFS = useFullScreenHandle();
  const { t: translate } = useTranslation();
  const previousMountedDataRef = useRef([]);
  const { value: sortEquipmentStatusFlag = '0' } = usePlatformConfiguration('sorting_equipment_status') || {};

  const [dataLoaded, setDataLoaded] = useState(false);

  const currentFilter = useSelector(
    state => state.dispatch.currentEquipmentStatusFilterV3,
    shallowEqual,
  );
  const equipmentTypes = useSelector(
    state => state.nref.equipmentTypes,
    shallowEqual,
  );
  const equipmentsGroups = useSelector(
    state => state.nref.equipmentGroups,
    shallowEqual,
  );
  const equipments = useSelector(
    state => state.nref.equipments,
    shallowEqual,
  );
  const elements = useSelector(state => state.nref.elements, shallowEqual);
  const subElements = useSelector(
    state => state.nref.subElements,
    shallowEqual,
  );

  const rawCodeTypes = useSelector(
    state => state.nref.codeTypes,
    shallowEqual,
  );
  const codeTypes = useMemo(
    () => rawCodeTypes.map(e => ({
      ...e,
      name: translate(`common:${e.name}`),
    })),
    [rawCodeTypes, translate],
  );
  const codeGroups = useSelector(
    state => state.nref.codeGroups,
    shallowEqual,
  );
  const rawCodes = useSelector(state => state.nref.codes, shallowEqual);

  const codes = useMemo(
    () => rawCodes.map(item => ({ ...item })),
    [rawCodes],
  );

  const codesByKey = useMemo(() => toCodesMapByKey(codes), [codes]);

  const equipmentTypesById = useMemo(
    () => Object.fromEntries(equipmentTypes.map(e => [e.id, e])),
    [equipmentTypes],
  );

  const equipmentGroupsById = useMemo(
    () => Object.fromEntries(equipmentsGroups.map(e => [e.id, e])),
    [equipmentsGroups],
  );

  const equipmentById = useMemo(
    () => Object.fromEntries(equipments.map(e => [e.id, e])),
    [equipments],
  );

  const rawEquipmentStatuses = useSelector(
    state => state.dispatch.equipmentStatuses,
    shallowEqual,
  );
  const highlightedEquipmentsStatus = useSelector(
    state => state.dispatch.highlightedEquipmentsStatus,
    shallowEqual,
  );

  const equipmentStatuses = useMemo(
    () => rawEquipmentStatuses.map(e => ({
      ...e,
      equip_name: equipmentById[String(e.id_equip)]?.name,
      code_name: codesByKey[buildCodeKey({
        id: e.id_code,
        equipType: e.equip_type,
        codeGroup: e.id_code_group,
        codeType: e.code_type,
      })]?.name,
      equip_type_name: equipmentTypesById[String(e.equip_type)]?.name,
      equip_group_name: equipmentGroupsById[String(e.id_equip_group)]?.name,
      code_type_name: codeTypes.find(eq => eq.id === e.code_type)?.name,
    })),
    [
      rawEquipmentStatuses,
      equipmentById,
      codesByKey,
      equipmentTypesById,
      equipmentGroupsById,
      codeTypes,
    ],
  );

  useEffect(() => {
    const savedFilters = getFilters('EquipmentStatus', 'user');
    if (savedFilters) {
      dispatch(setCurrentEquipmentStatusFilterV3(savedFilters));
    }
  }, [dispatch]);

  useEffect(() => {
    if (!dataLoaded) {
      dispatch(getCodes());
      dispatch(getCodeTypes());
      dispatch(getCodeGroups());
      dispatch(getEquipments());
      dispatch(getEquipmentTypes());
      dispatch(getEquipmentGroups());
      dispatch(getElements());
      dispatch(getSubElements());
      dispatch(getNrefOperators());
      dispatch(getClosures());

      setDataLoaded(true);
    }
  });

  const refreshBoard = useCallback(() => {
    dispatch(getEquipmentStatuses());
  }, [dispatch]);

  const { signal } = useThrottledRealtimeRefresh({
    refresh: refreshBoard,
    throttleMs: 1000,
  });

  useRealtimeSignal({
    topic: 'DISPATCHUPDATE',
    onSignal: signal,
  });

  const mountedData = useMemo(
    () => mountData(equipmentStatuses, previousMountedDataRef.current, sortEquipmentStatusFlag),
    [equipmentStatuses, sortEquipmentStatusFlag],
  );

  previousMountedDataRef.current = mountedData;

  const filteredEquipmentList = useMemo(
    () => filterEquipments(mountedData, currentFilter),
    [mountedData, currentFilter],
  );

  const filterOptions = useMemo(
    () => formatFilterOptions({
      equipments,
      equipmentTypes,
      equipmentsGroups,
      elements,
      subElements,
      codes,
      codeTypes,
      codeGroups,
      filters: currentFilter,
    }),
    [
      equipments,
      equipmentTypes,
      equipmentsGroups,
      elements,
      subElements,
      codes,
      codeTypes,
      codeGroups,
      currentFilter,
    ],
  );
  const {
    formFieldsFilter,
    handleFilterChange,
    handleClean,
    handleFilterChangeMultiple,
  } = useEquipmentStatusFilters(filterOptions);

  const visibleLegend = useMemo(
    () => currentFilter.isStatusEquipmentSubtitleOn,
    [currentFilter],
  );

  return (
    <FullScreen handle={handleFS}>
      <div className={classes.container}>
        <EquipmentStatusFilters
          handleFS={handleFS}
          formFields={formFieldsFilter}
          handleChangeMultiple={handleFilterChangeMultiple}
          handleChange={handleFilterChange}
          handleClean={handleClean}
          currentFilter={currentFilter}
        />
        <div
          className={classNames(
            classes.containerWrapper,
            handleFS.active && classes.containerWrapperFullScreen,
          )}
        >
          <div className={classes.containerEquip}>
            {visibleLegend && <EquipmentStatusLegend />}
            <EquipmentTypeList
              list={filteredEquipmentList}
              isFullScreenActive={handleFS?.active}
              highlightedEquipments={highlightedEquipmentsStatus}
            />
          </div>
        </div>
      </div>
    </FullScreen>
  );
});

export default withStyles(styles)(EquipmentStatusV3);

EquipmentStatusV3.propTypes = {
  classes: PropTypes.object.isRequired,
};
